import { useCallback, useEffect, useLayoutEffect, useState } from "react";

/**
 * Reports whether an element's content is currently being clipped — i.e. its
 * `scrollHeight` exceeds its `clientHeight`, as happens under a
 * `-webkit-line-clamp` truncation. Re-measures whenever the element's box size
 * changes (window resize, layout shifts, or the text content itself changing),
 * via `ResizeObserver`.
 *
 * Returns a callback ref rather than taking a `RefObject`, so measurement
 * re-attaches correctly when the measured node unmounts and remounts — as it
 * does when a "see more" control swaps the clamped text out and back in.
 *
 * Measurement runs in a layout effect on every commit, not just on resize.
 * Toggling a clamp on or off changes the element's overflow within the same
 * commit, and waiting for `ResizeObserver` to report it would let the consumer
 * paint one frame against a stale value — enough to make a gated control
 * disappear and reappear.
 *
 * Intended for gating that control so it only renders when the clamped text is
 * actually being cut off.
 *
 * @example
 * const [textRef, isClamped] = useIsClamped();
 * <div ref={textRef} style={CLAMP_STYLE}>{text}</div>
 * {isClamped && <Button label="See more" />}
 */
export const useIsClamped = (): [(node: HTMLElement | null) => void, boolean] => {
  const [node, setNode] = useState<HTMLElement | null>(null);
  const [isClamped, setIsClamped] = useState(false);

  const measuredRef = useCallback((element: HTMLElement | null) => {
    setNode(element);
  }, []);

  // Runs after every commit: re-measures synchronously before paint so a
  // clamp toggled in this render is reflected in the same frame. Setting the
  // same value bails out of re-rendering, so this cannot loop.
  useLayoutEffect(() => {
    setIsClamped(node ? node.scrollHeight > node.clientHeight : false);
  });

  // Covers size changes that happen outside React's commit cycle — window
  // resizes, font loads, sibling layout shifts.
  useEffect(() => {
    if (!node) return;

    const observer = new ResizeObserver(() => {
      setIsClamped(node.scrollHeight > node.clientHeight);
    });
    observer.observe(node);

    return () => observer.disconnect();
  }, [node]);

  return [measuredRef, isClamped];
};
